import { getAppointmentTypesCount } from '@frontend/api-appointment-types';
import { useTranslation } from '@frontend/i18n';
import { useQuery } from '@frontend/react-query-helpers';
import { settingsTrackingIds } from '@frontend/schedule-helpers';
import { SettingsLink, useSettingsNavigate } from '@frontend/settings-routing';
import { SettingsGroup } from '@frontend/settings-ui';
import { IndexRow, SpinningLoader, Text, styles } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';

export const MultiLocationAppointmentTypes = () => {
  const { navigate } = useSettingsNavigate();
  const { t } = useTranslation('schedule-settings');
  const { selectedLocationIds, getScopeName } = useSchedulingLocationInfo();

  const { data: appointmentTypesCountData, isLoading: isLoadingAppointmentTypesCount } = useQuery({
    queryKey: ['appointment-types-count', ...selectedLocationIds],
    queryFn: () =>
      getAppointmentTypesCount({
        locationIds: selectedLocationIds,
      }),

    cacheTime: 1000 * 60 * 5, // 5 minutes
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const appointmentTypesCountByLocationId = appointmentTypesCountData?.appointmentTypesCount?.reduce(
    (acc, { locationId, count }) => {
      if (locationId && !acc[locationId]) {
        acc[locationId] = count || 0;
      }
      return acc;
    },
    {} as { [key: string]: number }
  );

  const navigateTo = (locationId: string) => {
    const route = '/schedule/appointment-types/:locationId';

    navigate({
      to: route,
      params: { locationId: locationId ?? '' },
    });
  };

  if (isLoadingAppointmentTypesCount) {
    return (
      <div css={styles.flexCenter}>
        <SpinningLoader />
      </div>
    );
  }

  return (
    <SettingsGroup title={t('Location Settings')}>
      <IndexRow>
        {selectedLocationIds?.map((locationId) => (
          <IndexRow.Item
            key={locationId}
            as={SettingsLink}
            to='/schedule/appointment-types/:locationId'
            onClick={() => navigateTo(locationId)}
            trackingId={settingsTrackingIds.appointmentTypesLocationRowItem}
          >
            <IndexRow.Item.Title>
              <div css={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Text>{getScopeName(locationId)}</Text>
                <Text size='medium' color='light' css={{ marginLeft: 'auto' }}>
                  {t(`{{count}} Appointment Types`, { count: appointmentTypesCountByLocationId?.[locationId] ?? 0 })}
                </Text>
              </div>
            </IndexRow.Item.Title>
          </IndexRow.Item>
        ))}
      </IndexRow>
    </SettingsGroup>
  );
};
